import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Typography } from "@mui/material";
import { CASE_DELAY_SEVERITY } from "../../modules/dashboardModule/types/CommonTypes";
import EmptyComponent from "../EmptyComponent";
import { getChartColors } from "../../helpers/getChartColors";
import CustomTooltip from "../CustomTooltip";

const numGridLines = 5;
const colors = getChartColors();

interface StackChartProps {
  stackData: CASE_DELAY_SEVERITY[];
  showLagends?: boolean;
  showValues?: boolean;
  showTotal?: boolean;
  chartTitle?: string;
  tooltipTitle?: string;
}

type ChartData = {
  name: string;
  [key: string]: number | string;
};

const transformData = (caseDelaySeverityData: CASE_DELAY_SEVERITY[]) =>
  caseDelaySeverityData?.map((entry) => {
    const casesObject: { [key: string]: number } = {};
    entry.cases.forEach((caseItem) => {
      casesObject[`case${caseItem.index}`] = caseItem.delayMinutes;
    });
    return { name: entry.otName, cases: [casesObject] };
  });

const getUniqueCaseKeys = (data: ReturnType<typeof transformData>) =>
  Array.from(new Set(data?.flatMap((entry) => Object.keys(entry.cases[0] || {}))));

const processData = (data: ReturnType<typeof transformData>) => {
  const caseKeys = getUniqueCaseKeys(data);
  return data?.map((entry) => {
    const formattedEntry: ChartData = { name: entry.name };
    caseKeys.forEach((key) => {
      formattedEntry[key] = entry.cases[0]?.[key] || 0;
    });
    return formattedEntry;
  });
};

function showTooltip(
  chart: any,
  xScale: any,
  yScale: any,
  value: number,
  label: string
) {
  chart.selectAll(".bar-tooltip").remove();

  const tooltipGroup = chart
    .append("g")
    .attr("class", "bar-tooltip")
    .style("pointer-events", "none");

  const xPos = (xScale(label) ?? 0) + xScale.bandwidth() / 2;
  const yPos = yScale(value) - 16;

  const tooltipContent = tooltipGroup
    .append("g")
    .attr("transform", `translate(${xPos},${yPos})`);

  const text = tooltipContent
    .append("text")
    .style("font-size", "12px")
    .style("fill", "#fff")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .text(`${value}`);

  const bbox = (text.node() as SVGTextElement).getBBox();

  tooltipContent
    .insert("rect", "text")
    .attr("x", bbox.x - 8)
    .attr("y", bbox.y - 4)
    .attr("width", bbox.width + 16)
    .attr("height", bbox.height + 8)
    .attr("rx", 5)
    .attr("ry", 5)
    .style("fill", "#1a1832")
    .style("opacity", 0.95);

  const arrowSize = 6;
  const arrowX = 0;
  const arrowY = bbox.y + bbox.height + 3;

  tooltipContent
    .append("polygon")
    .attr(
      "points",
      `
        ${arrowX - arrowSize},${arrowY}
        ${arrowX + arrowSize},${arrowY}
        ${arrowX},${arrowY + arrowSize}
      `
    )
    .style("fill", "#1a1832")
    .style("opacity", 0.95);
}


function hideTooltip(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) {
  svg.selectAll(".bar-tooltip").remove();
}


const StackChart: React.FC<StackChartProps> = ({
  stackData,
  showValues = true,
  showTotal = true,
  showLagends = true,
  chartTitle = "Stacked Chart",
  tooltipTitle,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const legendRef = useRef<SVGSVGElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [legendWidth, setLegendWidth] = useState(0);

  const transformedStackData = transformData(stackData);
  const data = processData(transformedStackData);
  const CASE_KEYS = getUniqueCaseKeys(transformedStackData);

  const drawChart = () => {
    if (!svgRef.current || !legendRef.current || !chartContainerRef.current) return;

    const containerWidth = chartContainerRef.current.clientWidth;
    const height = chartContainerRef.current.clientHeight;
    const width = containerWidth;
    const margin = { top: 40, right: 50, bottom: 40, left: 50 };

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const g = svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const stack = d3.stack().keys(CASE_KEYS);
    const stackedData = stack(data as any);

    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, width - margin.left - margin.right])
      .padding(0.4);

    const yMax = d3.max(stackedData, (d) => d3.max(d, (d) => d[1])) || 0;
    const yScale = d3
      .scaleLinear()
      .domain([0, yMax > 0 ? yMax : 400])
      .nice()
      .range([height - margin.top - margin.bottom, 0]);

    g.append("g")
      .attr("transform", `translate(0,${height - margin.top - margin.bottom})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll("text")
      .attr("font-size", "13px")
      .attr("fill", "#00000066")
      .attr("dy", "20px");

    const yAxis = g.append("g").call(
      d3.axisLeft(yScale).ticks(numGridLines).tickSize(-width + margin.left + margin.right)
    );

    yAxis.selectAll("line").attr("stroke", "#D6D6D6").attr("stroke-width", 1.5);
    yAxis.select(".domain").remove();
    yAxis.selectAll("text").attr("font-size", "13px").attr("fill", "#00000066");

    const layers = g
      .selectAll(".layer")
      .data(stackedData)
      .enter()
      .append("g")
      .attr("fill", (_, i) => colors[i % colors.length]);

    layers
      .selectAll("rect")
      .data((d) => d)
      .enter()
      .append("rect")
      .attr("x", (d: any) => xScale(d.data.name)!)
      .attr("y", (d) => yScale(d[1]))
      .attr("height", (d) => yScale(d[0]) - yScale(d[1]))
      .attr("width", xScale.bandwidth())
      .on("mouseover", function (event, d: any) {
        const parent = d.data;
        const total = Object.values(parent).slice(1).reduce((a: number, b: any) => a + +b, 0);

        showTooltip(g, xScale, yScale, total, parent.name);
      })
      .on("mouseout", function () {
        const svgNode = d3.select(svgRef.current) as d3.Selection<SVGSVGElement, unknown, null, undefined>;
        hideTooltip(svgNode);
      });

    if (showValues) {
      layers
        .selectAll("text")
        .data((d) => d)
        .enter()
        .append("text")
        .attr("x", (d: any) => xScale(d.data.name)! + xScale.bandwidth() / 2)
        .attr("y", (d) => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2 + 5)
        .attr("text-anchor", "middle")
        .attr("fill", "#000000")
        .attr("font-size", "12px")
        .text((d) => {
          const value = d[1] - d[0];
          return value > 0 ? Math.round(value) : "";
        });
    }

    if (showTotal) {
      g.selectAll(".totalLabel")
        .data(data)
        .enter()
        .append("text")
        .attr("x", (d) => xScale(d.name)! + xScale.bandwidth() / 2)
        .attr(
          "y",
          (d) =>
            yScale(
              (Object.values(d).slice(1) as number[]).reduce(
                (a, b) => a + Math.round(b),
                0
              )
            ) - 10
        )
        .attr("text-anchor", "middle")
        .attr("fill", "#000000")
        .attr("font-size", "12px")
        .text((d) =>
          (Object.values(d).slice(1) as number[]).reduce(
            (a, b) => a + Math.round(b),
            0
          )
        );
    }

    if (showLagends && chartContainerRef.current) {
      const itemWidth = 120;
      const itemHeight = 25;
      const titleWidth = 400;
      const availableWidth = containerWidth - titleWidth + 90;

      const itemsPerRow = Math.max(1, Math.floor(availableWidth / itemWidth));
      const numRows = Math.ceil(CASE_KEYS.length / itemsPerRow);
      const legendHeight = numRows * itemHeight;

      setLegendWidth(availableWidth);

      const legend = d3.select(legendRef.current);
      legend.selectAll("*").remove();
      legend.attr("height", legendHeight).attr("width", availableWidth);

      const legendG = legend.append("g");

      CASE_KEYS.forEach((caseKey, index) => {
        const row = Math.floor(index / itemsPerRow);
        const col = index % itemsPerRow;
        const xOffset =
          availableWidth -
          Math.min(CASE_KEYS.length, itemsPerRow) * itemWidth +
          col * itemWidth;
        const yOffset = row * itemHeight;

        const legendItem = legendG
          .append("g")
          .attr("transform", `translate(${xOffset}, ${yOffset})`);

        legendItem
          .append("circle")
          .attr("cx", 10)
          .attr("cy", 6)
          .attr("r", 6)
          .attr("fill", colors[index % colors.length]);

        legendItem
          .append("text")
          .attr("x", 25)
          .attr("y", 10)
          .attr("fill", "#555")
          .attr("font-size", "14px")
          .text(caseKey.replace("case", "Case "));
      });
    }
  };

  useEffect(() => {
    drawChart(); // Initial draw

    const observer = new ResizeObserver(() => drawChart());
    chartContainerRef.current && observer.observe(chartContainerRef.current);
    return () => observer.disconnect();
  }, [data, CASE_KEYS]);

  return (
    <Box flex={1} display="flex" flexDirection="column" sx={{ height: "100%" }}>
      <Box display="flex" flexDirection="row" alignItems="flex-start">
        <Box sx={{ mr: 2 }}>
          <CustomTooltip title={tooltipTitle}>
            <Typography variant="h6" color="#5B5B5B">
              {chartTitle}
            </Typography>
          </CustomTooltip>
        </Box>
        {showLagends && CASE_KEYS.length > 0 && data.length > 0 && (
          <Box flex={1}>
            <svg ref={legendRef} height={40} width={legendWidth}></svg>
          </Box>
        )}
      </Box>
      {data?.length > 0 ? (
        <Box
          ref={chartContainerRef}
          sx={{ height: "23rem", display: "flex", flexDirection: "column" }}
        >
          <svg ref={svgRef} style={{ width: "100%", flex: 1 }}></svg>
        </Box>
      ) : (
        <EmptyComponent />
      )}
    </Box>
  );
};

export default StackChart;
